import { createAgentSchema, modelSupportsTemperature, type Agent, type Tool } from '@agents/shared';
import { getRouteApi, Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Icon } from '../components/icons';
import { useCan } from '../components/shell';
import {
  Alert,
  BackLink,
  Button,
  ConfirmDialog,
  Disclosure,
  ErrorState,
  Field,
  PageHeader,
  SkeletonRows,
  SwitchRow,
  useToast,
} from '../components/ui';
import { ApiProblem } from '../lib/api';
import { useDocumentTitle } from '../lib/document-title';
import { clearError } from '../lib/forms';
import { useAgent, useKnowledgeSources, useModels, useSaveAgent, useTools } from '../lib/queries';

const editRoute = getRouteApi('/_app/agentes/$agentId/editar');

interface FormState {
  name: string;
  description: string;
  instructions: string;
  model: string;
  temperature: string;
  maxToolCalls: string;
  toolIds: string[];
  knowledgeSourceIds: string[];
  redactPersonalData: boolean;
}

/** Field keys as the API reports them, with the label used in the error summary. */
const FIELD_LABEL: Record<string, string> = {
  name: 'Nombre',
  description: 'Descripción',
  instructions: 'Instrucciones',
  'config.model': 'Modelo',
  'config.temperature': 'Temperatura',
  'config.maxToolCalls': 'Máximo de llamadas a herramientas',
  toolIds: 'Herramientas',
  sourceIds: 'Conocimiento',
};

/** Which API error key each form field clears when edited. */
const ERROR_KEY: Record<keyof FormState, string> = {
  name: 'name',
  description: 'description',
  instructions: 'instructions',
  model: 'config.model',
  temperature: 'config.temperature',
  maxToolCalls: 'config.maxToolCalls',
  toolIds: 'toolIds',
  knowledgeSourceIds: 'sourceIds',
  redactPersonalData: 'config.dataPolicy.redactPersonalData',
};

function initialState(agent: Agent | undefined, defaultModel: string): FormState {
  return {
    name: agent?.name ?? '',
    description: agent?.description ?? '',
    instructions: agent?.instructions ?? '',
    model: agent?.config.model ?? defaultModel,
    temperature: String(agent?.config.temperature ?? 0.3),
    maxToolCalls: String(agent?.config.maxToolCalls ?? 10),
    toolIds: agent?.toolIds ?? [],
    knowledgeSourceIds: agent?.knowledgeSourceIds ?? [],
    redactPersonalData: agent?.config.dataPolicy?.redactPersonalData ?? false,
  };
}

const sameState = (a: FormState, b: FormState) =>
  JSON.stringify({
    ...a,
    toolIds: [...a.toolIds].sort(),
    knowledgeSourceIds: [...a.knowledgeSourceIds].sort(),
  }) ===
  JSON.stringify({
    ...b,
    toolIds: [...b.toolIds].sort(),
    knowledgeSourceIds: [...b.knowledgeSourceIds].sort(),
  });

function ToolGroup({
  kind,
  tools,
  selected,
  onToggle,
}: {
  kind: Tool['kind'];
  tools: Tool[];
  selected: string[];
  onToggle: (id: string, on: boolean) => void;
}) {
  if (!tools.length) return null;
  const action = kind === 'action';
  return (
    <fieldset className="choice-group">
      <legend>
        <Icon name={action ? 'zap' : 'eye'} />
        {action ? 'Acciones' : 'Consultas'}
        <small>· {action ? 'pueden modificar otros sistemas' : 'solo leen información'}</small>
      </legend>
      {tools.map((t) => (
        <label key={t.id} className="choice">
          <input
            type="checkbox"
            checked={selected.includes(t.id)}
            onChange={(e) => onToggle(t.id, e.target.checked)}
          />
          <span>
            <strong>{t.name}</strong>
            <small>{t.description}</small>
            {action && <small className="kind-note">Puede cambiar datos en otros sistemas.</small>}
          </span>
          <span className="chip">{t.id}</span>
        </label>
      ))}
    </fieldset>
  );
}

function KnowledgeSection({
  sources,
  selected,
  error,
  onChange,
}: {
  sources: ReturnType<typeof useKnowledgeSources>;
  selected: string[];
  error?: string;
  onChange: (ids: string[]) => void;
}) {
  const own = sources.data?.filter((s) => s.visibility === 'agent') ?? [];
  const shared = sources.data?.filter((s) => s.visibility === 'tenant') ?? [];
  return (
    <section className="form-section">
      <header>
        <h2>Conocimiento</h2>
        <p>Documentos que el agente consulta para responder. Solo usa las fuentes que marques.</p>
      </header>
      <div data-field="sourceIds" tabIndex={-1} style={{ outline: 'none' }}>
        {sources.isPending && <SkeletonRows rows={2} />}
        {sources.isError && <ErrorState error={sources.error} onRetry={() => void sources.refetch()} />}
        {sources.data && !sources.data.length && (
          <p className="muted" style={{ margin: 0 }}>
            Aún no hay fuentes de conocimiento. <Link to="/conocimiento">Agrega textos o documentos</Link>{' '}
            para que tus agentes los consulten.
          </p>
        )}
        {own.length > 0 && (
          <fieldset className="choice-group">
            <legend>
              <Icon name="book" />
              Fuentes
            </legend>
            {own.map((s) => (
              <label key={s.id} className="choice">
                <input
                  type="checkbox"
                  checked={selected.includes(s.id)}
                  onChange={(e) =>
                    onChange(e.target.checked ? [...selected, s.id] : selected.filter((x) => x !== s.id))
                  }
                />
                <span>
                  <strong>{s.name}</strong>
                  <small>
                    {s.readyCount === 1 ? '1 documento listo' : `${s.readyCount} documentos listos`}
                    {s.documentCount > s.readyCount && ` · ${s.documentCount - s.readyCount} en proceso`}
                  </small>
                </span>
                <span />
              </label>
            ))}
          </fieldset>
        )}
        {shared.length > 0 && (
          <p className="muted" style={{ margin: '8px 0 0' }}>
            Además, todos los agentes consultan{' '}
            {shared.length === 1 ? 'la fuente compartida' : 'las fuentes compartidas'}:{' '}
            {shared.map((s) => s.name).join(', ')}.
          </p>
        )}
        {error && (
          <div className="field">
            <p className="error">
              <Icon name="alert" />
              {error}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function AgentForm({
  agent,
  models,
  defaultModel,
}: {
  agent?: Agent;
  models: string[];
  defaultModel: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const can = useCan();
  const tools = useTools();
  const sources = useKnowledgeSources();
  const save = useSaveAgent();
  const [initial] = useState(() => initialState(agent, defaultModel));
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const summaryRef = useRef<HTMLDivElement>(null);
  const leaving = useRef(false);

  const dirty = !sameState(form, initial);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const blocker = useBlocker({
    shouldBlockFn: () => dirtyRef.current && !leaving.current,
    enableBeforeUnload: () => dirtyRef.current && !leaving.current,
    withResolver: true,
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    // A corrected field stops showing its error; the rest stay until the next submit.
    setErrors(clearError(ERROR_KEY[key]));
  };

  const errorKeys = Object.keys(errors);
  // Move focus to the summary only when a submit fails, never while the user is typing.
  const [focusSummary, setFocusSummary] = useState(0);
  useEffect(() => {
    if (focusSummary) summaryRef.current?.focus();
  }, [focusSummary]);
  const showErrors = (next: Record<string, string>) => {
    setErrors(next);
    if (Object.keys(next).length) setFocusSummary((n) => n + 1);
  };

  const focusField = (key: string) => {
    if (key.startsWith('config.')) document.getElementById('advanced')?.setAttribute('open', '');
    document.querySelector<HTMLElement>(`[data-field="${key}"]`)?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = createAgentSchema.safeParse({
      name: form.name,
      description: form.description,
      instructions: form.instructions,
      config: {
        model: form.model,
        temperature: Number(form.temperature),
        maxToolCalls: Number(form.maxToolCalls),
        dataPolicy: { redactPersonalData: form.redactPersonalData },
      },
    });
    if (!parsed.success) {
      showErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])));
      return;
    }
    setErrors({});
    try {
      const saved = await save.mutateAsync({
        id: agent?.id,
        input: parsed.data,
        toolIds: can('tool.grant') ? form.toolIds : undefined,
        knowledgeSourceIds: can('knowledge.read') ? form.knowledgeSourceIds : undefined,
      });
      toast(
        agent ? 'Cambios guardados' : 'Agente creado',
        agent
          ? { detail: 'Publica una nueva versión para usarlos en ejecuciones.' }
          : { detail: 'Se guardó como borrador. Publícalo para ejecutarlo.' },
      );
      leaving.current = true;
      await navigate({ to: '/agentes/$agentId', params: { agentId: saved.id } });
    } catch (err) {
      if (err instanceof ApiProblem) showErrors(err.fieldErrors());
    }
  };

  const available = tools.data?.filter((t) => t.status === 'available') ?? [];
  const tempDisabled = !modelSupportsTemperature(form.model);
  const configError = errorKeys.some((k) => k.startsWith('config.'));

  return (
    <form className="form" onSubmit={submit} noValidate>
      {save.isError && !errorKeys.length && <ErrorState error={save.error} />}
      {errorKeys.length > 0 && (
        <div ref={summaryRef} tabIndex={-1} className="page-alert" style={{ outline: 'none' }}>
          <Alert
            tone="danger"
            role="alert"
            title={
              errorKeys.length === 1
                ? 'Revisa 1 campo antes de guardar'
                : `Revisa ${errorKeys.length} campos antes de guardar`
            }
          >
            <ul>
              {errorKeys.map((k) => (
                <li key={k}>
                  <a
                    href={`#${k}`}
                    onClick={(e) => {
                      e.preventDefault();
                      focusField(k);
                    }}
                  >
                    {FIELD_LABEL[k] ?? k}: {errors[k]}
                  </a>
                </li>
              ))}
            </ul>
          </Alert>
        </div>
      )}

      <section className="form-section">
        <header>
          <h2>Identidad</h2>
          <p>Cómo reconocerán este agente las personas de tu equipo.</p>
        </header>
        <div>
          <Field label="Nombre" error={errors.name}>
            {(p) => (
              <input
                {...p}
                data-field="name"
                className="input"
                value={form.name}
                maxLength={80}
                placeholder="Ej.: Asistente de soporte"
                required
                onChange={(e) => set('name', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Descripción"
            optional
            help="Una frase sobre para qué sirve. Aparece en la lista de agentes."
            error={errors.description}
          >
            {(p) => (
              <input
                {...p}
                data-field="description"
                className="input"
                value={form.description}
                maxLength={500}
                onChange={(e) => set('description', e.target.value)}
              />
            )}
          </Field>
        </div>
      </section>

      <section className="form-section">
        <header>
          <h2>Instrucciones</h2>
          <p>Qué debe hacer, con qué tono y qué no debe hacer nunca.</p>
        </header>
        <div>
          <Field
            label="Instrucciones del agente"
            help="Incluye el rol, las tareas, el formato de respuesta y los límites."
            foot={
              <span className="mono muted">{form.instructions.length.toLocaleString('es')} caracteres</span>
            }
            error={errors.instructions}
          >
            {(p) => (
              <textarea
                {...p}
                data-field="instructions"
                className="textarea"
                rows={14}
                placeholder="Eres el asistente de… Tu tarea es… Responde en… Nunca…"
                value={form.instructions}
                onChange={(e) => set('instructions', e.target.value)}
              />
            )}
          </Field>
        </div>
      </section>

      {can('tool.grant') && (
        <section className="form-section">
          <header>
            <h2>Herramientas</h2>
            <p>El agente solo puede usar las que marques. Revisa con cuidado las acciones.</p>
          </header>
          <div data-field="toolIds" tabIndex={-1} style={{ outline: 'none' }}>
            {tools.isPending && <SkeletonRows rows={2} />}
            {tools.isError && <ErrorState error={tools.error} onRetry={() => void tools.refetch()} />}
            {tools.data && !available.length && (
              <p className="muted" style={{ margin: 0 }}>
                Todavía no hay herramientas disponibles en este espacio.
              </p>
            )}
            {(['read', 'action'] as const).map((kind) => (
              <ToolGroup
                key={kind}
                kind={kind}
                tools={available.filter((t) => t.kind === kind)}
                selected={form.toolIds}
                onToggle={(id, on) =>
                  set('toolIds', on ? [...form.toolIds, id] : form.toolIds.filter((x) => x !== id))
                }
              />
            ))}
            {errors.toolIds && (
              <div className="field">
                <p className="error">
                  <Icon name="alert" />
                  {errors.toolIds}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {can('knowledge.read') && (
        <KnowledgeSection
          sources={sources}
          selected={form.knowledgeSourceIds}
          error={errors.sourceIds}
          onChange={(ids) => set('knowledgeSourceIds', ids)}
        />
      )}

      <section className="form-section">
        <header>
          <h2>Privacidad</h2>
          <p>Qué datos se ocultan antes de enviarlos al modelo de lenguaje.</p>
        </header>
        <div>
          <div className="fact-inline">
            <Icon name="shield" />
            <span>Siempre se ocultan números de tarjeta, contraseñas, llaves y tokens.</span>
          </div>
          <SwitchRow
            title="Ocultar también datos personales"
            description="Correos, teléfonos y números de documento se reemplazan por marcadores. El agente no podrá repetirlos ni usarlos en su respuesta."
            checked={form.redactPersonalData}
            onChange={(v) => set('redactPersonalData', v)}
          />
        </div>
      </section>

      <section className="form-section">
        <header>
          <h2>Ajustes avanzados</h2>
          <p>Valores recomendados para la mayoría de los casos.</p>
        </header>
        <div>
          <Disclosure
            id="advanced"
            defaultOpen={configError}
            title="Modelo y límites"
            meta={`${form.model}${tempDisabled ? '' : ` · temp. ${form.temperature}`} · ${form.maxToolCalls} herramientas`}
          >
            {models.length > 1 ? (
              <Field label="Modelo" error={errors['config.model']}>
                {(p) => (
                  <select
                    {...p}
                    data-field="config.model"
                    className="select"
                    value={form.model}
                    onChange={(e) => set('model', e.target.value)}
                  >
                    {models.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            ) : (
              <div className="field">
                <span className="label">Modelo</span>
                <div className="fact-inline" data-field="config.model" tabIndex={-1}>
                  <span className="mono">{form.model}</span>
                  <span className="muted">· único modelo disponible en tu espacio</span>
                </div>
              </div>
            )}
            <div className="row">
              <Field
                label="Temperatura"
                help={
                  tempDisabled ? 'Este modelo no admite temperatura.' : '0 es más preciso; 1, más creativo.'
                }
                error={errors['config.temperature']}
              >
                {(p) => (
                  <input
                    {...p}
                    data-field="config.temperature"
                    className="input"
                    type="number"
                    min={0}
                    max={2}
                    step={0.1}
                    disabled={tempDisabled}
                    value={form.temperature}
                    onChange={(e) => set('temperature', e.target.value)}
                  />
                )}
              </Field>
              <Field
                label="Máximo de llamadas a herramientas"
                help="Por ejecución. Evita bucles costosos."
                error={errors['config.maxToolCalls']}
              >
                {(p) => (
                  <input
                    {...p}
                    data-field="config.maxToolCalls"
                    className="input"
                    type="number"
                    min={0}
                    max={50}
                    value={form.maxToolCalls}
                    onChange={(e) => set('maxToolCalls', e.target.value)}
                  />
                )}
              </Field>
            </div>
          </Disclosure>
        </div>
      </section>

      <div className="form-bar">
        <span className="dirty" data-dirty={dirty} aria-live="polite">
          {dirty ? <span>Cambios sin guardar</span> : 'Sin cambios'}
        </span>
        <div className="actions">
          {agent ? (
            <Link to="/agentes/$agentId" params={{ agentId: agent.id }} className="btn btn-ghost">
              Descartar
            </Link>
          ) : (
            <Link to="/agentes" className="btn btn-ghost">
              Cancelar
            </Link>
          )}
          <Button variant="primary" type="submit" loading={save.isPending}>
            {agent ? 'Guardar cambios' : 'Crear agente'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => !open && blocker.reset?.()}
        title="¿Salir sin guardar los cambios?"
        description={
          agent
            ? `Perderás los cambios que hiciste en ${agent.name}. Esta acción no se puede deshacer.`
            : 'Perderás el agente que estabas creando. Esta acción no se puede deshacer.'
        }
        cancelLabel="Seguir editando"
        confirmLabel="Descartar cambios"
        onConfirm={() => blocker.proceed?.()}
      />
    </form>
  );
}

export function NewAgentPage() {
  useDocumentTitle('Nuevo agente');
  const models = useModels();
  return (
    <>
      <PageHeader
        back={<BackLink to="/agentes">Agentes</BackLink>}
        title="Nuevo agente"
        description="Se guarda como borrador. Podrás ejecutarlo cuando lo publiques."
      />
      {models.isPending && <SkeletonRows />}
      {models.isError && <ErrorState error={models.error} onRetry={() => void models.refetch()} />}
      {models.data && <AgentForm models={models.data.items} defaultModel={models.data.default} />}
    </>
  );
}

export function EditAgentPage() {
  const { agentId } = editRoute.useParams();
  const agent = useAgent(agentId);
  const models = useModels();
  useDocumentTitle(agent.data ? `Editar ${agent.data.name}` : 'Editar agente');
  const error = agent.error ?? models.error;
  return (
    <>
      <PageHeader
        back={
          <BackLink to="/agentes/$agentId" params={{ agentId }}>
            {agent.data?.name ?? 'Agente'}
          </BackLink>
        }
        title={agent.data ? `Editar ${agent.data.name}` : 'Editar agente'}
        description={
          agent.data?.currentVersion
            ? `Los cambios quedan en borrador. Las ejecuciones siguen usando v${agent.data.currentVersion} hasta que publiques una nueva versión.`
            : 'Los cambios quedan en borrador hasta que publiques el agente.'
        }
      />
      {(agent.isPending || models.isPending) && !error && <SkeletonRows />}
      {error && <ErrorState error={error} />}
      {agent.data && models.data && (
        <AgentForm agent={agent.data} models={models.data.items} defaultModel={models.data.default} />
      )}
    </>
  );
}
